import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CFDropdownItem, CFDropdownItemType, CFDropdownOptions, CfDropdownComponent } from '../cf-dropdown/cf-dropdown.component';
import { CfTypeaheadComponent } from '../cf-typeahead/cf-typeahead.component';
import { CfLoaderComponent, CFLoaderSize, CFLoaderStyle, CFLoaderColor } from '../cf-loader/cf-loader.component';
import { CommonModule } from '@angular/common';
import { RoleName, User } from '@aifs-shared/user/user';
import { MenuAction, MenuActionType, MenuService } from './menu.service';
import { Subject, takeUntil } from 'rxjs';
import { PortalUserPermission, PortalUserService } from '@aifs-shared/portal/portal-user-service';
import { UserService } from '@aifs-shared/user/user.service';
import { CacheService } from '@aifs-shared/cache/cache.service';
import { ApplicationCountry, InterviewerService } from '@aifs-shared/interviewer/interviewer.service';
import { ApplicantService, ApplicantSearchByStringResponse, SearchByStringResult, RecentlyViewedApplicantsResponse, RecentlyViewedApplicant, InterviewerSearchByStringResponse, InterviewerSearchResult } from '../../applicant/applicant.service';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import { RoundImageDisplayComponent } from '../../shared/upload/round-image-display.component';

@Component({
    selector: 'app-cf-menu',
    standalone: true,
    templateUrl: './cf-menu.component.html',
    styleUrls: ['./cf-menu.component.scss'],
    imports: [CommonModule, CfDropdownComponent, CfTypeaheadComponent, CfLoaderComponent, RoundImageDisplayComponent]
})
export class CfMenuComponent implements OnInit, OnChanges, OnDestroy {
    @Input() user: User | undefined;
    @Input() showAll = true;
    @Output() menuSelected: EventEmitter<string> = new EventEmitter();

    @ViewChild('userMenu') menu?: ElementRef;
    @ViewChild('typeahead', { static: false }) typeahead!: CfTypeaheadComponent;

    isSignedIn = false;
    userName = '';

    private unsubscribe = new Subject<void>();

    constructor(
        private cd: ChangeDetectorRef,
        private userService: UserService,
        private portalUser: PortalUserService,
        private menuService: MenuService,
        private cache: CacheService,
        private interviewerService: InterviewerService,
        private applicantService: ApplicantService,
        private router: Router) {

    }

    ngOnInit(): void {
        this.menuService.getData$()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe({
                next: (data: MenuAction) => {
                    console.log(`Data: ${JSON.stringify(data)}`);
                    switch (data.actionType) {
                        case MenuActionType.HideAll:
                            this.showAll = false;
                            break;
                        case MenuActionType.ShowAll:
                            this.showAll = true;
                            break;
                        case MenuActionType.Refresh:
                            break;
                    }
                    this.cd.detectChanges();
                }
            });
        this.loadMenu();

        this.userService.userChanged.subscribe(
            (user: User | undefined) => {
                this.user = user;
                this.loadMenu();
            });
    }

    loadMenu(): void {
        let isAgentOrAbove = this.portalUser.isInRoleOrAbove(RoleName.Agent);
        let isInterviewerOrAbove = this.portalUser.isInRoleOrAbove(RoleName.Interviewer);
        let isOnlineInterviewer = isInterviewerOrAbove && (this.userService.interviewerRole()?.isOnlineInterviewer ?? false);

        this.assignedWorkOptions = {
            text: 'My work', showIndicator: false
        };

        this.calendarOptions = undefined;
        if (isInterviewerOrAbove && (isOnlineInterviewer || this.canViewInstantBook)) {
            this.calendarOptions = { text: 'Calendar', showIndicator: false };
        }

        this.interviewerOptions = undefined;
        if (isAgentOrAbove && this.portalUser.hasPermission(PortalUserPermission.ViewInterviewer)) {
            this.interviewerOptions = {
                text: 'Interviewers', showIndicator: true, items: [
                    { type: CFDropdownItemType.ImageAndText, text: 'Interviewers', link: 'interviewer/users', imageClass: 'fa-solid fa-people-group' },
                    { type: CFDropdownItemType.ImageAndText, text: 'Summary', link: 'interviewer/summary', imageClass: 'fa-solid fa-list-check' },
                ]
            };
        }

        this.dropdownOptions = undefined;
        if (isAgentOrAbove) {
            let canMaintainUsers = this.portalUser.hasPermission(PortalUserPermission.ViewAbeonaUsers);
            let canMaintainCountries = this.portalUser.hasPermission(PortalUserPermission.MaintainCountry);
            let canMaintainWorkQueues = this.portalUser.hasPermission(PortalUserPermission.MaintainWorkQueue);
            let canMaintainPermissions = this.portalUser.hasPermission(PortalUserPermission.ViewPermissions);

            let mi = [];

            if (canMaintainUsers) {
                mi.push(
                    { type: CFDropdownItemType.ImageAndText, text: 'Users', link: 'maintain/user', imageClass: 'fa-solid fa-circle-user' },
                    { type: CFDropdownItemType.Separator },
                );
            }

            if (canMaintainWorkQueues) {
                mi.push(
                    { type: CFDropdownItemType.ImageAndText, text: 'Work Area', link: 'work-area', imageClass: 'fa-solid fa-circle-user' },
                    { type: CFDropdownItemType.Separator },
                );
            }

            if (canMaintainCountries) {
                mi.push(
                    { type: CFDropdownItemType.ImageAndText, text: 'Countries', link: 'maintain/country', imageClass: 'fa-solid fa-book-atlas' },
                    { type: CFDropdownItemType.Separator },
                );
            }

            if (canMaintainPermissions) {
                mi.push(
                    { type: CFDropdownItemType.ImageAndText, text: 'Permissions', link: 'maintain/permission', imageClass: 'fa-solid fa-user-shield' },
                    { type: CFDropdownItemType.Separator },
                );
            }

            mi.push({ type: CFDropdownItemType.ImageAndText, text: 'Flights', link: 'maintain/flight', imageClass: 'fa-solid fa-plane', disabled: true },);

            this.dropdownOptions = {
                text: '', imageClass: 'fa-solid fa-user-gear', showIndicator: true, items: mi
            };
        }

        let userItems: CFDropdownItem[] = [];
        if (isInterviewerOrAbove) {
            userItems.push(
                { type: CFDropdownItemType.ImageAndText, text: 'Details', link: '/interviewer/details', imageClass: 'fa-solid fa-circle-info' },
                { type: CFDropdownItemType.Separator },
            );
        }
        userItems.push(
            { type: CFDropdownItemType.ImageAndText, text: 'Profile', link: 'profile', imageClass: 'fa-solid fa-id-card' },
            { type: CFDropdownItemType.Separator },
            { type: CFDropdownItemType.ImageAndText, text: 'Messages', link: 'messages', imageClass: 'fa-regular fa-envelope' },
            { type: CFDropdownItemType.Separator },
            { type: CFDropdownItemType.ImageAndText, text: 'Sign out', link: 'sign-out', imageClass: 'fa-solid fa-right-from-bracket' },
        );

        this.userOptions = {
            imageClass: 'fa-solid fa-circle-user',
            showIndicator: false,
            leftMargin: '-11.5rem',
            items: userItems
        };
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["user"]) {
            this.isSignedIn = this.user !== undefined;
            // console.log(`Got signed in user: ${this.isSignedIn}: ${this.user?.firstName}`);

            this.userName = this.isSignedIn ? this.user?.firstName ?? '' : '';
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    doTypeaheadLookup(value: string): void {
        this.currentSearchText = value;

        // Clear results if search term is too short
        if (!value || value.trim().length < 3) {
            this.typeaheadSearchResults = [];
            this.interviewerSearchResults = [];
            this.alreadySearched = false;
            return;
        }

        this.alreadySearched = false;
        this.isTypeaheadSearching = true;

        // Applicant search
        if (this.selectedTab === 'applicant') {
            this.applicantService
                .searchByString(value)
                .pipe(takeUntil(this.unsubscribe))
                .subscribe({
                    next: (response: ApplicantSearchByStringResponse) => {
                        this.typeaheadSearchResults = response.customerApplications || [];
                        this.isTypeaheadSearching = false;
                        this.currentSearchResultsForText = value;
                        this.alreadySearched = true;
                    },
                    error: (error: any) => {
                        console.error('Search error:', error);
                        this.typeaheadSearchResults = [];
                        this.isTypeaheadSearching = false;
                        this.currentSearchResultsForText = value;
                        this.alreadySearched = true;
                    }
                });
        } else {
            // Interviewer search
            this.applicantService
                .searchInterviewersByString(value)
                .pipe(takeUntil(this.unsubscribe))
                .subscribe({
                    next: (response: InterviewerSearchByStringResponse) => {
                        this.interviewerSearchResults = response.interviewers || [];
                        this.isTypeaheadSearching = false;
                        this.currentSearchResultsForText = value;
                        this.alreadySearched = true;
                    },
                    error: (error: any) => {
                        console.error('Interviewer search error:', error);
                        this.interviewerSearchResults = [];
                        this.isTypeaheadSearching = false;
                        this.currentSearchResultsForText = value;
                        this.alreadySearched = true;
                    }
                });
        }
    }

    onSearchFocus(): void {
        this.loadRecentlyViewed();
    }

    loadRecentlyViewed(): void {
        const user = this.userService.user();
        if (!user) return;

        this.applicantService
            .getRecentlyViewedApplicants(user.id)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe({
                next: (response: RecentlyViewedApplicantsResponse) => {
                    this.recentlyViewedApplicants = response.applicantsViewed || [];
                },
                error: (error: any) => {
                    console.error('Error loading recently viewed:', error);
                }
            });
    }

    canShowHistory(): boolean {
        if (this.selectedTab === 'applicant') {
            return (!this.typeaheadSearchResults || this.typeaheadSearchResults.length === 0);
        }
        return false;
    }

    selectRecentlyViewed(applicant: RecentlyViewedApplicant): void {
        this.typeahead.closeResults();
        if (applicant.applicationId) {
            const url = `/applicant/${applicant.applicantId}/${applicant.applicationId}`;
            window.open(url, '_blank');
        } else {
            const url = `/applicant/${applicant.applicantId}`;
            window.open(url, '_blank');
        }
    }

    selectSearchResult(result: SearchByStringResult): void {
        this.typeahead.closeResults();
        const url = `/applicant/${result.applicantId}/${result.applicationId}`;
        window.open(url, '_blank');
    }

    selectInterviewer(interviewer: InterviewerSearchResult): void {
        this.typeahead.closeResults();
        const url = `/interviewer/${interviewer.id}`;
        window.open(url, '_blank');
    }

    getStatusClass(userState: string): string {
        const statusClass = this.applicantService.getStateDisplayClass(userState);
        // Handle the case where the service returns an object instead of string (bug in service)
        return typeof statusClass === 'string' ? `status ${statusClass}` : 'status defaultStatus';
    }

    selectTab(tab: 'applicant' | 'interviewer'): void {
        this.selectedTab = tab;
        // Re-run search for the new tab if we have search text
        if (this.currentSearchText && this.currentSearchText.length >= 3) {
            this.doTypeaheadLookup(this.currentSearchText);
        }
    }

    itemSelected(link: string): void {
        this.menuSelected.emit(link);
    }

    dropdownSelected(item: CFDropdownItem): void {
        this.menuSelected.emit(item.link);
    }

    private CACHE_COUNTRY_DURATION_MS = 1000 * 60 * 10;
    private getCountryForInterviewer(): void {
        const code = this.userService.user()?.countryCode;

        if (code) {
            const c = this.cache.getWithExpiry<ApplicationCountry>('i-c');
            if (c) {
                // console.log(`Got cached`);
                if (c.code === code) {
                    this.updateViewInstantBook(c);
                    return;
                }
            }
            // console.log(`No cache item; read from db`);
            this.interviewerService
                .getCountry(code)
                .subscribe({
                    next: (c: ApplicationCountry) => {
                        this.cache.setWithExpiry('i-c', c, this.CACHE_COUNTRY_DURATION_MS);
                        this.updateViewInstantBook(c);
                    },
                    error: (error: any) => {
                        console.error(error);
                    }
                });
        }
    }

    private updateViewInstantBook(country: ApplicationCountry): void {
        this.canViewInstantBook = country.onlineInterviewsMigration;
    }

    assignedWorkOptions?: CFDropdownOptions;
    calendarOptions?: CFDropdownOptions;
    interviewerOptions?: CFDropdownOptions;
    dropdownOptions?: CFDropdownOptions;
    userOptions?: CFDropdownOptions;

    isTypeaheadSearching = false;
    typeaheadRecordSelected = false;
    typeaheadSearchResults: SearchByStringResult[] = [];
    interviewerSearchResults: InterviewerSearchResult[] = [];
    recentlyViewedApplicants: RecentlyViewedApplicant[] = [];
    recentlyViewedInterviewers: InterviewerSearchResult[] = [];
    selectedTab: 'applicant' | 'interviewer' = 'applicant';
    alreadySearched = false;
    currentSearchText = '';
    currentSearchResultsForText = '';

    canViewInstantBook = false;
    noProfileImage = environment.resources.imageNoProfileUrl;

    LoaderSize = CFLoaderSize;
    LoaderStyle = CFLoaderStyle;
    LoaderColor = CFLoaderColor;
}

